import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  DataGrid,
  GridColDef,
  GridColumnGroupingModel,
  GridRenderCellParams,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import {
  Box,
  Tooltip,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Typography,
  Divider,
  Collapse,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  TrainingTableData,
  TrainingTableRow,
  TrainingTableCellProgress,
  TrainingContentProgressDto,
} from '../../api/types/dtos/training';
import { createTrainingProgressService } from '../../api/services/training-progress.service';
import LoadingSpinner from '../../components/LoadingSpinner';
import ProgressEditModal from './components/ProgressEditModal';
import BatchEnrollmentModal from './modals/BatchEnrollmentModal';
import styles from './TrainingProgressTable.module.scss';

interface TrainingProgressTableProps {
  data?: TrainingTableData;
  loading?: boolean;
  onRefetch?: () => void;
  canEdit?: boolean;
  canCreate?: boolean;
}

type CellState = Record<string, { isCompleted: boolean }>;

const TrainingProgressTable: React.FC<TrainingProgressTableProps> = ({
  data,
  loading = false,
  onRefetch,
  canEdit = false,
  canCreate = false,
}) => {
  const [cellState, setCellState] = useState<CellState>({});
  const [updating, setUpdating] = useState<Record<string, boolean>>({});
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProgress, setEditingProgress] = useState<TrainingContentProgressDto | null>(null);
  const [editingOrgName, setEditingOrgName] = useState('');
  const [editingContentName, setEditingContentName] = useState('');
  const [editingOrganizationId, setEditingOrganizationId] = useState('');
  // Store enrollmentId and contentId separately to prevent stale state issues
  const [editingEnrollmentId, setEditingEnrollmentId] = useState<string>('');
  const [editingContentId, setEditingContentId] = useState<string>('');
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [enrollmentModalOpen, setEnrollmentModalOpen] = useState(false);
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  // Debounced invalidation
  const pendingInvalidationRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setCellState({});
    setSelectedCells(new Set());
    setEditModalOpen(false);
    setEditingProgress(null);
    setEditingEnrollmentId('');
    setEditingContentId('');
  }, [data]);

  useEffect(() => {
    return () => {
      if (pendingInvalidationRef.current) {
        clearTimeout(pendingInvalidationRef.current);
      }
    };
  }, []);

  const scheduleRefetch = useCallback(() => {
    if (pendingInvalidationRef.current) {
      clearTimeout(pendingInvalidationRef.current);
    }
    pendingInvalidationRef.current = setTimeout(() => {
      if (onRefetch) {
        onRefetch();
      } else {
        queryClient.invalidateQueries({
          queryKey: ['training-progress', selectedOrganization?.id],
        });
      }
      pendingInvalidationRef.current = null;
    }, 300);
  }, [onRefetch, queryClient, selectedOrganization]);

  // Mutation for updating individual progress
  const updateProgressMutation = useMutation({
    mutationFn: async ({
      enrollmentId,
      contentId,
      progressId,
      existingProgress,
      updateData,
    }: {
      enrollmentId: string;
      contentId: string;
      progressId?: string;
      existingProgress?: TrainingContentProgressDto;
      updateData: {
        isCompleted: boolean;
        score?: number;
        evidences?: boolean;
        completedAt?: string;
        isActive?: boolean;
      };
    }) => {
      const progressService = createTrainingProgressService(selectedOrganization?.id || '', {
        enrollmentId,
      });

      // Check if we have a valid progress ID (not empty string or undefined)
      const hasValidProgressId = progressId && progressId.trim() !== '';
      
      if (hasValidProgressId && existingProgress) {
        // Update existing progress - include all existing fields
        return progressService.update(progressId!, {
          enrollmentId: enrollmentId,
          contentId: contentId,
          isCompleted: updateData.isCompleted ?? existingProgress.isCompleted,
          score: updateData.score ?? existingProgress.score,
          evidences: updateData.evidences ?? existingProgress.evidences,
          completedAt: updateData.completedAt ?? existingProgress.completedAt,
          isActive: updateData.isActive ?? existingProgress.isActive,
        });
      } else {
        // Create new progress
        return progressService.create({
          enrollmentId,
          contentId,
          isCompleted: updateData.isCompleted,
          score: updateData.score,
          evidences: updateData.evidences,
          isActive: updateData.isActive ?? true,
        });
      }
    },
    onMutate: async (variables) => {
      // Optimistic update
      const cellKey = `${variables.enrollmentId}-${variables.contentId}`;
      setCellState((prev) => ({
        ...prev,
        [cellKey]: { isCompleted: variables.updateData.isCompleted },
      }));
      return { cellKey };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.cellKey) {
        setCellState((prev) => {
          const newState = { ...prev };
          delete newState[context.cellKey];
          return newState;
        });
      }
      console.error('Error updating progress:', _error);
    },
    onSettled: () => {
      scheduleRefetch();
    },
  });

  // Batch mutation for multiple progress updates
  const batchUpdateMutation = useMutation({
    mutationFn: async ({
      trainingId,
      contentId,
      organizationIds,
      isCompleted,
    }: {
      trainingId: string;
      contentId: string;
      organizationIds: string[];
      isCompleted: boolean;
    }) => {
      const progressService = createTrainingProgressService(selectedOrganization?.id || '', {});
      return progressService.batchUpdateProgress(trainingId, contentId, {
        organizationIds,
        isCompleted,
      });
    },
    onSuccess: () => {
      setCellState({});
      setSelectedCells(new Set());
      if (onRefetch) {
        onRefetch();
      } else {
        queryClient.invalidateQueries({
          queryKey: ['training-progress', selectedOrganization?.id],
        });
      }
    },
    onError: (error) => {
      console.error('Error batch updating progress:', error);
    },
  });

  const handleCellClick = useCallback(
    (enrollmentId: string, contentId: string, currentProgress: TrainingTableCellProgress) => {
      const cellKey = `${enrollmentId}-${contentId}`;
      const updateKey = cellKey;
      
      setUpdating((u) => ({ ...u, [updateKey]: true }));

      const currentIsCompleted = cellState[cellKey]?.isCompleted ?? currentProgress.isCompleted;
      const nextIsCompleted = !currentIsCompleted;

      // Check if we have a valid progress ID
      const hasValidProgressId = currentProgress.progressId && currentProgress.progressId.trim() !== '';
      
      // Construct existingProgress if we have a valid progressId
      const existingProgress: TrainingContentProgressDto | undefined = hasValidProgressId
        ? {
            id: currentProgress.progressId!,
            enrollmentId,
            contentId,
            isCompleted: currentProgress.isCompleted,
            score: currentProgress.score,
            evidences: false,
            completedAt: undefined,
            isActive: true,
          }
        : undefined;

      updateProgressMutation.mutate(
        {
          enrollmentId,
          contentId,
          progressId: currentProgress.progressId,
          existingProgress,
          updateData: {
            isCompleted: nextIsCompleted,
            completedAt: nextIsCompleted ? new Date().toISOString() : undefined,
          },
        },
        {
          onSettled: () => {
            setUpdating((u) => {
              const newU = { ...u };
              delete newU[updateKey];
              return newU;
            });
          },
        }
      );
    },
    [cellState, updateProgressMutation]
  );

  const handleEditClick = useCallback(
    (
      enrollmentId: string,
      contentId: string,
      currentProgress: TrainingTableCellProgress,
      orgName: string,
      contentName: string,
      organizationId: string
    ) => {
      // Create a full progress object
      const progressData: TrainingContentProgressDto = {
        id: currentProgress.progressId || '',
        enrollmentId,
        contentId,
        isCompleted: currentProgress.isCompleted,
        score: currentProgress.score,
        evidences: false,
        completedAt: undefined,
        isActive: true,
      };

      setEditingProgress(progressData);
      setEditingOrgName(orgName);
      setEditingContentName(contentName);
      setEditingOrganizationId(organizationId);
      // Store enrollmentId and contentId separately to ensure they match the cell being edited
      setEditingEnrollmentId(enrollmentId);
      setEditingContentId(contentId);
      setEditModalOpen(true);
    },
    []
  );

  const handleSaveEdit = useCallback(
    async (updated: Partial<TrainingContentProgressDto>) => {
      if (!editingProgress) return;

      // Validate that enrollmentId and contentId match the stored values
      // This prevents stale state from causing wrong IDs to be persisted
      const enrollmentId = editingEnrollmentId || editingProgress.enrollmentId;
      const contentId = editingContentId || editingProgress.contentId;
      
      // Additional validation: ensure IDs are present
      if (!enrollmentId || !contentId) {
        console.error('Missing enrollmentId or contentId in handleSaveEdit');
        return;
      }

      const cellKey = `${enrollmentId}-${contentId}`;
      setUpdating((u) => ({ ...u, [cellKey]: true }));

      try {
        // Check if progress exists (has a valid ID)
        const hasExistingProgress = editingProgress.id && editingProgress.id.trim() !== '';
        
        await updateProgressMutation.mutateAsync(
          {
            enrollmentId,
            contentId,
            progressId: hasExistingProgress ? editingProgress.id : undefined,
            existingProgress: hasExistingProgress ? editingProgress : undefined,
            updateData: {
              isCompleted: updated.isCompleted ?? editingProgress.isCompleted,
              score: updated.score,
              evidences: updated.evidences,
              completedAt: updated.completedAt,
              isActive: updated.isActive,
            },
          }
        );
      } finally {
        setUpdating((u) => {
          const newU = { ...u };
          delete newU[cellKey];
          return newU;
        });
      }
    },
    [editingProgress, editingEnrollmentId, editingContentId, updateProgressMutation]
  );

  const handleCellSelect = useCallback((cellKey: string, isSelected: boolean) => {
    setSelectedCells((prev) => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(cellKey);
      } else {
        newSet.delete(cellKey);
      }
      return newSet;
    });
  }, []);

  const handleBatchComplete = useCallback(() => {
    if (!data || selectedCells.size === 0) return;

    // Group selected cells by content
    const contentMap = new Map<string, string[]>();

    selectedCells.forEach((cellKey) => {
      const [enrollmentId, contentId] = cellKey.split('-');
      // Find organization ID from enrollment
      const row = data.rows.find((r) => r.enrollmentId === enrollmentId);
      if (row) {
        if (!contentMap.has(contentId)) {
          contentMap.set(contentId, []);
        }
        contentMap.get(contentId)!.push(row.organizationId);
      }
    });

    // Execute batch update for each content
    contentMap.forEach((orgIds, contentId) => {
      batchUpdateMutation.mutate({
        trainingId: data.trainingId,
        contentId,
        organizationIds: orgIds,
        isCompleted: true,
      });
    });
  }, [data, selectedCells, batchUpdateMutation]);

  const handleBatchIncomplete = useCallback(() => {
    if (!data || selectedCells.size === 0) return;

    const contentMap = new Map<string, string[]>();

    selectedCells.forEach((cellKey) => {
      const [enrollmentId, contentId] = cellKey.split('-');
      const row = data.rows.find((r) => r.enrollmentId === enrollmentId);
      if (row) {
        if (!contentMap.has(contentId)) {
          contentMap.set(contentId, []);
        }
        contentMap.get(contentId)!.push(row.organizationId);
      }
    });

    contentMap.forEach((orgIds, contentId) => {
      batchUpdateMutation.mutate({
        trainingId: data.trainingId,
        contentId,
        organizationIds: orgIds,
        isCompleted: false,
      });
    });
  }, [data, selectedCells, batchUpdateMutation]);

  // Helper to get all content field names for a module
  const getModuleContentFields = useCallback(
    (moduleId: string): string[] => {
      if (!data) return [];
      const module = data.modules.find((m) => m.moduleId === moduleId);
      if (!module) return [];
      return module.topics.flatMap((topic) =>
        topic.contents.map((content) => `content_${content.contentId}`)
      );
    },
    [data]
  );

  // Helper to get all content field names for a topic
  const getTopicContentFields = useCallback(
    (topicId: string): string[] => {
      if (!data) return [];
      for (const module of data.modules) {
        const topic = module.topics.find((t) => t.topicId === topicId);
        if (topic) {
          return topic.contents.map((content) => `content_${content.contentId}`);
        }
      }
      return [];
    },
    [data]
  );

  // Check if a module is fully visible
  const isModuleVisible = useCallback(
    (moduleId: string): boolean => {
      const fields = getModuleContentFields(moduleId);
      if (fields.length === 0) return true;
      return fields.every((field) => columnVisibilityModel[field] !== false);
    },
    [getModuleContentFields, columnVisibilityModel]
  );

  // Check if a topic is fully visible
  const isTopicVisible = useCallback(
    (topicId: string): boolean => {
      const fields = getTopicContentFields(topicId);
      if (fields.length === 0) return true;
      return fields.every((field) => columnVisibilityModel[field] !== false);
    },
    [getTopicContentFields, columnVisibilityModel]
  );

  // Toggle module visibility
  const handleToggleModule = useCallback(
    (moduleId: string) => {
      const fields = getModuleContentFields(moduleId);
      const isVisible = isModuleVisible(moduleId);
      setColumnVisibilityModel((prev) => {
        const next = { ...prev };
        fields.forEach((field) => {
          next[field] = !isVisible;
        });
        return next;
      });
    },
    [getModuleContentFields, isModuleVisible]
  );

  // Toggle topic visibility
  const handleToggleTopic = useCallback(
    (topicId: string) => {
      const fields = getTopicContentFields(topicId);
      const isVisible = isTopicVisible(topicId);
      setColumnVisibilityModel((prev) => {
        const next = { ...prev };
        fields.forEach((field) => {
          next[field] = !isVisible;
        });
        return next;
      });
    },
    [getTopicContentFields, isTopicVisible]
  );

  // Toggle module expansion
  const handleToggleModuleExpand = useCallback((moduleId: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  }, []);

  const handleDownloadCsv = useCallback(() => {
    if (!data) return;

    const allContents = data.modules.flatMap((m) =>
      m.topics.flatMap((t) => t.contents)
    );

    const headers = [
      'Organization Name',
      'Group',
      'Progress %',
      ...allContents.map((c) => c.contentName),
    ];

    const csvRows = data.rows.map((row) => {
      const rowData = [
        row.organizationName,
        row.groupName || '-',
        `${row.progressPercentage}%`,
        ...allContents.map((content) => {
          const cellKey = `${row.enrollmentId}-${content.contentId}`;
          const progress = cellState[cellKey] ?? row.contentProgress[content.contentId];
          return progress?.isCompleted ? 'Completed' : 'Incomplete';
        }),
      ];
      return rowData;
    });

    const csvContent = [headers, ...csvRows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `training_progress_${data.trainingName}_${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [data, cellState]);

  // Generate columns
  const columns = useMemo<GridColDef[]>(() => {
    if (!data) return [];

    const baseColumns: GridColDef[] = [
      {
        field: 'organizationName',
        headerName: 'Organization',
        width: 180,
        sortable: false,
      },
      {
        field: 'adminUserName',
        headerName: 'Admin Name',
        width: 180,
        valueGetter: (_value, row) => row.adminUserName || '-',
        sortable: false,
      },
      {
        field: 'adminUserIdNumber',
        headerName: 'ID Number',
        width: 120,
        valueGetter: (_value, row) => row.adminUserIdNumber || '-',
        sortable: false,
      },
      {
        field: 'regionName',
        headerName: 'Region',
        width: 140,
        valueGetter: (_value, row) => row.regionName || '-',
        sortable: false,
      },
      {
        field: 'adminUserPhones',
        headerName: 'Phones',
        width: 160,
        valueGetter: (_value, row) => row.adminUserPhones?.join(', ') || '-',
        sortable: false,
      },
      {
        field: 'groupName',
        headerName: 'Group',
        width: 130,
        valueGetter: (_value, row) => row.groupName || '-',
        sortable: false,
      },
      {
        field: 'progressPercentage',
        headerName: 'Progress',
        width: 100,
        valueGetter: (_value, row) => `${row.progressPercentage}%`,
        sortable: false,
      },
    ];

    // Generate content columns
    const contentColumns: GridColDef[] = [];
    data.modules.forEach((module) => {
      module.topics.forEach((topic) => {
        topic.contents.forEach((content) => {
          contentColumns.push({
            field: `content_${content.contentId}`,
            headerName: content.contentSequence.toString(),
            width: 100,
            headerAlign: 'center',
            align: 'center',
            renderHeader: () => (
              <Tooltip
                title={
                  <span style={{ fontSize: '1rem' }}>
                    {content.contentName}
                  </span>
                }
              >
                <span style={{ cursor: 'help' }}>
                  {content.contentSequence}
                </span>
              </Tooltip>
            ),
            renderCell: (params: GridRenderCellParams) => {
              const row = params.row as TrainingTableRow;
              const cellKey = `${row.enrollmentId}-${content.contentId}`;
              const progress =
                cellState[cellKey] ?? row.contentProgress[content.contentId];
              const isCompleted = progress?.isCompleted || false;
              const isUpdating = updating[cellKey] || false;
              const isSelected = selectedCells.has(cellKey);

              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    padding: '4px',
                    backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
                    borderRadius: '4px',
                    '&:hover .edit-btn': {
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      cursor: (canEdit || canCreate) ? 'pointer' : 'default',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={(e) => {
                      if (!(canEdit || canCreate)) return;
                      if (e.shiftKey || e.ctrlKey || e.metaKey) {
                        handleCellSelect(cellKey, !isSelected);
                      } else {
                        handleCellClick(row.enrollmentId, content.contentId, progress);
                      }
                    }}
                  >
                    {isUpdating ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px' }}>...</span>
                      </Box>
                    ) : isCompleted ? (
                      <Tooltip title="Completed - Click to mark as incomplete">
                        <CheckCircleIcon color="success" fontSize="small" />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Incomplete - Click to mark as complete">
                        <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                      </Tooltip>
                    )}
                  </Box>
                  {canEdit && (
                  <Tooltip title="Edit details">
                    <IconButton
                      className="edit-btn"
                      size="small"
                      sx={{
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        padding: '2px',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(
                          row.enrollmentId,
                          content.contentId,
                          progress,
                          row.organizationName,
                          content.contentName,
                          row.organizationId
                        );
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  )}
                </Box>
              );
            },
          });
        });
      });
    });

    return [...baseColumns, ...contentColumns];
  }, [data, cellState, updating, selectedCells, handleCellClick, handleCellSelect, canEdit, canCreate]);

  // Generate column grouping
  const columnGroupingModel = useMemo<GridColumnGroupingModel>(() => {
    if (!data) return [];

    const grouping: GridColumnGroupingModel = [];

    data.modules.forEach((module) => {
      const moduleChildren: any[] = [];

      module.topics.forEach((topic) => {
        const topicChildren = topic.contents.map((c) => ({
          field: `content_${c.contentId}`,
        }));

        moduleChildren.push({
          groupId: `topic_${topic.topicId}`,
          headerName: topic.topicName,
          children: topicChildren,
        });
      });

      grouping.push({
        groupId: `module_${module.moduleId}`,
        headerName: module.moduleName,
        children: moduleChildren,
      });
    });

    return grouping;
  }, [data]);

  if (loading && !data) {
    return <LoadingSpinner />;
  }

  if (!data) {
    return <Box>No data available</Box>;
  }

  return (
      <Box className={styles.container}>
      <Box className={styles.toolbar}>
        <Box className={styles.toolbarLeft}>
          {(canEdit || canCreate) && selectedCells.size > 0 && (
            <>
              <span className={styles.selectedCount}>
                {selectedCells.size} cell{selectedCells.size > 1 ? 's' : ''} selected
              </span>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={handleBatchComplete}
                disabled={batchUpdateMutation.isPending}
              >
                Mark Complete
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleBatchIncomplete}
                disabled={batchUpdateMutation.isPending}
              >
                Mark Incomplete
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => setSelectedCells(new Set())}
              >
                Clear Selection
              </Button>
            </>
          )}
        </Box>
        <Box className={styles.toolbarRight}>
          <Tooltip title="Enroll Users or Groups">
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={() => setEnrollmentModalOpen(true)}
              disabled={!data || !canCreate}
            >
              Enroll
            </Button>
          </Tooltip>
          <Tooltip title="Column Visibility">
            <IconButton onClick={() => setColumnSelectorOpen(true)} size="small">
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download CSV">
            <IconButton onClick={handleDownloadCsv} size="small">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <DataGrid
        rows={data.rows}
        columns={columns}
        getRowId={(row) => row.enrollmentId}
        columnGroupingModel={columnGroupingModel}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        disableRowSelectionOnClick
        disableColumnSorting
        hideFooter
        autoHeight
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            fontSize: '0.875rem',
          },
          '& .MuiDataGrid-cell:first-of-type': {
            position: 'sticky',
            left: 0,
            backgroundColor: 'background.paper',
            zIndex: 1,
          },
        }}
      />

      {/* Column Visibility Drawer */}
      <Drawer
        anchor="right"
        open={columnSelectorOpen}
        onClose={() => setColumnSelectorOpen(false)}
        PaperProps={{
          sx: { width: 350 },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Column Visibility</Typography>
            <IconButton size="small" onClick={() => setColumnSelectorOpen(false)}>
              <ClearIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {data && (
            <List dense>
              {data.modules.map((module) => {
                const isModuleExpanded = expandedModules.has(module.moduleId);
                const moduleVisible = isModuleVisible(module.moduleId);
                return (
                  <React.Fragment key={module.moduleId}>
                    <ListItem
                      sx={{
                        px: 0,
                        py: 0.5,
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <Checkbox
                        checked={moduleVisible}
                        indeterminate={
                          !moduleVisible &&
                          module.topics.some((topic) => isTopicVisible(topic.topicId))
                        }
                        onChange={() => handleToggleModule(module.moduleId)}
                        size="small"
                      />
                      <ListItemText
                        primary={module.moduleName}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          fontSize: '0.95rem',
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleToggleModuleExpand(module.moduleId)}
                      >
                        {isModuleExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </ListItem>
                    <Collapse in={isModuleExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding dense>
                        {module.topics.map((topic) => {
                          const topicVisible = isTopicVisible(topic.topicId);
                          return (
                            <ListItem
                              key={topic.topicId}
                              sx={{
                                pl: 4,
                                py: 0.5,
                                '&:hover': { backgroundColor: 'action.hover' },
                              }}
                            >
                              <Checkbox
                                checked={topicVisible}
                                onChange={() => handleToggleTopic(topic.topicId)}
                                size="small"
                              />
                              <ListItemText
                                primary={topic.topicName}
                                primaryTypographyProps={{
                                  fontSize: '0.875rem',
                                }}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Drawer>

      {/* Edit Modal */}
      <ProgressEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        progress={editingProgress}
        organizationName={editingOrgName}
        contentName={editingContentName}
        organizationId={editingOrganizationId}
        enrollmentId={editingEnrollmentId || editingProgress?.enrollmentId || ''}
        onSave={handleSaveEdit}
      />

      {/* Enrollment Modal */}
      {data && (
        <BatchEnrollmentModal
          open={enrollmentModalOpen}
          onClose={() => {
            setEnrollmentModalOpen(false);
            if (onRefetch) {
              onRefetch();
            }
          }}
          trainingId={data.trainingId}
        />
      )}
    </Box>
  );
};

export default TrainingProgressTable;

